/**
 * Centralized API Configuration
 * 
 * This file contains all API-related constants and endpoints.
 * Instead of duplicating API_BASE_URL across 20+ files, we centralize it here.
 */

const getApiBaseUrl = (): string => {
  const runtimeValue =
    typeof window !== 'undefined'
      ? (window as any).REACT_APP_SECRETS?.REACT_APP_API_BASE_URL
      : undefined;
  const envValue = process.env.REACT_APP_API_BASE_URL;
  const baseUrl = (runtimeValue || envValue || 'https://mentis-server.onrender.com').trim();
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * API Endpoints
 * Centralized endpoint paths for consistency
 */
export const API_ENDPOINTS = {
  // Video Analysis
  analyze: '/api/v1/analyze',
  processUserVideo: '/api/v1/process-user-video',
  bucketFiles: (userId: string) => `/api/v1/user/${userId}/bucket-files`,
  bucketFile: (userId: string, videoName: string) => `/api/v1/user/${userId}/bucket-files/${videoName}`,
  
  // Recommendations
  generateRecommendations: '/api/v1/generate-recommendations',
  
  // Status and Runs
  activeRuns: (userId: string) => `/api/v1/active-runs/${userId}`,
  status: (requestId: string) => `/api/v1/status/${requestId}`,
  
  // User Management
  userFiles: (userId: string) => `/api/v1/user/${userId}/files`,
  
  // Video Agent
  videoAgentChat: '/api/v1/video-agent/chat',
  videoAgentConversations: '/api/v1/video-agent/conversations',

  // Free tools — niche analyzers per platform
  nicheAnalyzerYoutube: '/api/v1/free-tools/youtube-niche-analyzer',
  nicheAnalyzerTiktok: '/api/v1/free-tools/tiktok-niche-analyzer',
  nicheAnalyzerInstagram: '/api/v1/free-tools/instagram-niche-analyzer',
  nicheAnalyzerFacebook: '/api/v1/free-tools/facebook-niche-analyzer',
  nicheAnalyzerX: '/api/v1/free-tools/x-niche-analyzer',
} as const;

/**
 * Build full API URL
 * @param endpoint - The endpoint path
 * @returns Full URL with base URL prepended
 */
export function buildApiUrl(endpoint: string): string {
  return `${API_BASE_URL}${endpoint}`;
}

/**
 * Common fetch options
 */
export const DEFAULT_FETCH_OPTIONS: RequestInit = {
  headers: {
    'Content-Type': 'application/json',
  },
};

/**
 * API request timeout in milliseconds
 */
export const API_TIMEOUT = 8000;

